import React from 'react';
import { Table, Tag, Progress, Badge, Space, Typography, Tooltip } from 'antd';
import { EnvironmentOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { AreaPerformance } from '../../../types/dashboard';
import { formatNumber, formatRupiah, formatPercent, getProgressColor, glassTableContainerStyle } from '../utils';

const { Title, Text } = Typography;

interface AreaPerformanceTableProps {
  performanceByArea: AreaPerformance[];
  loading: boolean;
  sortBy: string;
  sortOrder: string;
  onTableChange: (pagination: any, filters: any, sorter: any) => void;
}

export const AreaPerformanceTable: React.FC<AreaPerformanceTableProps> = ({ 
  performanceByArea, 
  loading,
  sortBy,
  sortOrder,
  onTableChange
}) => {
  // Map antd sorting order to desc/asc or undefined
  const getAntdSortOrder = (colKey: string) => {
    if (sortBy === colKey) {
      return sortOrder === 'asc' ? 'ascend' as const : 'descend' as const;
    }
    return undefined;
  };

  // Generate a clean sorting label for the subtitle
  const getSortingLabel = () => {
    let colName = 'Nama Kawasan';
    if (sortBy === 'leads') colName = 'Realisasi Leads';
    if (sortBy === 'unit') colName = 'Realisasi Unit';
    if (sortBy === 'omzet') colName = 'Realisasi Omzet';
    
    const direction = sortOrder === 'asc' 
      ? (sortBy === 'name' ? 'A-Z' : 'Terendah')
      : (sortBy === 'name' ? 'Z-A' : 'Tertinggi');
      
    return `${colName} (${direction})`;
  };

  const areaColumns = [
    {
      title: (
        <Text strong style={{ color: sortBy === 'name' ? '#1890ff' : 'inherit' }}>
          Kawasan
        </Text>
      ),
      dataIndex: 'property_area_name',
      key: 'property_area_name',
      sorter: true,
      sortOrder: getAntdSortOrder('name'),
      showSorterTooltip: false,
      render: (text: string) => (
        <Space>
          <EnvironmentOutlined style={{ color: '#7BAA18' }} />
          <Typography.Text strong>{text}</Typography.Text>
        </Space>
      ),
    },
    {
      title: (
        <Space size={4}>
          <span>Target Leads</span>
          <Tooltip title="Target jumlah perolehan calon pembeli (leads) tahunan yang dicanangkan Kantor Pusat khusus untuk wilayah kawasan ini.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      ),
      key: 'leads_target',
      align: 'right' as const,
      render: (record: AreaPerformance) => formatNumber(record.leads.target),
    },
    {
      title: (
        <Space size={4}>
          <Text strong style={{ color: sortBy === 'leads' ? '#1890ff' : 'inherit' }}>
            Realisasi Leads
          </Text>
          <Tooltip title="Jumlah perolehan calon pembeli (leads) aktif riil yang masuk ke dalam sistem khusus untuk kawasan ini sepanjang periode filter, beserta persentase ketercapaiannya terhadap target tahunan.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      ),
      key: 'leads_realization',
      align: 'right' as const,
      sorter: true,
      sortOrder: getAntdSortOrder('leads'),
      showSorterTooltip: false,
      render: (record: AreaPerformance) => (
        <Space direction="vertical" align="end" size={0}>
          <Typography.Text>{formatNumber(record.leads.realization)}</Typography.Text>
          <Progress
            percent={record.leads.percentage}
            size="small"
            status={record.leads.percentage >= 100 ? "success" : "active"}
            strokeColor={getProgressColor(record.leads.percentage)}
            style={{ width: 80, margin: 0 }}
          />
        </Space>
      ),
    },
    {
      title: (
        <Space size={4}>
          <span>Target Unit</span>
          <Tooltip title="Target jumlah unit properti yang harus terjual (closing approved) tahunan khusus di kawasan ini.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      ),
      key: 'unit_target',
      align: 'right' as const,
      render: (record: AreaPerformance) => formatNumber(record.unit.target),
    },
    {
      title: (
        <Space size={4}>
          <Text strong style={{ color: sortBy === 'unit' ? '#1890ff' : 'inherit' }}>
            Realisasi Unit
          </Text>
          <Tooltip title="Jumlah unit properti yang telah sukses terjual (closing berstatus approved) khusus di kawasan ini sepanjang periode filter, beserta persentase ketercapaiannya terhadap target tahunan.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      ),
      key: 'unit_realization',
      align: 'right' as const,
      sorter: true,
      sortOrder: getAntdSortOrder('unit'),
      showSorterTooltip: false,
      render: (record: AreaPerformance) => (
        <Space direction="vertical" align="end" size={0}>
          <Typography.Text>{formatNumber(record.unit.realization)}</Typography.Text>
          <Progress
            percent={record.unit.percentage}
            size="small"
            status={record.unit.percentage >= 100 ? "success" : "active"}
            strokeColor={getProgressColor(record.unit.percentage)}
            style={{ width: 80, margin: 0 }}
          />
        </Space>
      ),
    },
    {
      title: (
        <Space size={4}>
          <span>Target Omzet</span>
          <Tooltip title="Target nilai nominal transaksi penjualan tahunan yang dibebankan Kantor Pusat khusus di kawasan ini.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      ),
      key: 'omzet_target',
      align: 'right' as const,
      render: (record: AreaPerformance) => formatRupiah(record.omzet.target),
    },
    {
      title: (
        <Space size={4}>
          <Text strong style={{ color: sortBy === 'omzet' ? '#1890ff' : 'inherit' }}>
            Realisasi Omzet
          </Text>
          <Tooltip title="Total nilai transaksi riil dari pemesanan unit yang telah sukses disetujui (approved) oleh Kantor Pusat khusus di kawasan ini sepanjang periode filter, beserta persentase ketercapaiannya terhadap target tahunan.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      ),
      key: 'omzet_realization',
      align: 'right' as const,
      sorter: true,
      sortOrder: getAntdSortOrder('omzet'),
      showSorterTooltip: false,
      render: (record: AreaPerformance) => (
        <Space direction="vertical" align="end" size={0}>
          <Typography.Text strong>{formatRupiah(record.omzet.realization)}</Typography.Text>
          <Progress
            percent={record.omzet.percentage}
            size="small"
            status={record.omzet.percentage >= 100 ? "success" : "normal"}
            strokeColor={getProgressColor(record.omzet.percentage)}
            style={{ width: 100, margin: 0 }}
          />
        </Space>
      ),
    },
    {
      title: (
        <Space size={4}>
          <span>SLA Compliance</span>
          <Tooltip title="Rasio ketepatan waktu respon rata-rata seluruh sales agent di bawah naungan kawasan ini dalam menindaklanjuti leads yang ditugaskan kepada mereka.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'sla_compliance_rate',
      key: 'sla_compliance_rate',
      align: 'center' as const,
      render: (rate: number) => (
        <Badge
          count={formatPercent(rate)}
          style={{
            backgroundColor: rate >= 85 ? '#52c41a' : rate >= 70 ? '#faad14' : '#ff4d4f',
            borderRadius: '4px',
            padding: '0 8px',
            fontSize: '12px',
          }}
        />
      ),
    },
  ];

  return (
    <div style={glassTableContainerStyle} className="glass-table-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <Space size={6} direction="vertical" style={{ width: 'auto' }}>
          <Space size={6}>
            <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
              Performa Pencapaian berdasarkan Kawasan
            </Title>
            <Tooltip title="Tabel komparatif pencapaian Target tahunan (Leads, Unit, Omzet) terhadap Realisasi riilnya, serta rata-rata kepatuhan respon SLA di masing-masing kawasan kerja yang resmi Anda kelola.">
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '13px', cursor: 'pointer' }} />
            </Tooltip>
          </Space>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', margin: 0 }}>
            Urutan aktif: <Text strong style={{ color: '#1890ff' }}>{getSortingLabel()}</Text>
          </Text>
        </Space>
        <Tag color="green">{performanceByArea?.length || 0} Kawasan Aktif</Tag>
      </div>
      <Table
        dataSource={performanceByArea}
        columns={areaColumns}
        rowKey="property_area_id"
        pagination={{ pageSize: 5 }}
        onChange={onTableChange}
        bordered={false}
        size="middle"
        style={{ background: 'transparent' }}
        className="glass-table"
        loading={loading}
      />
    </div>
  );
};

export default AreaPerformanceTable;
